import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SCREEN_OCCUPANCY } from '../data/mockData';
type TimePeriod = 'Morning' | 'Afternoon' | 'Evening' | 'Night';
const TIME_PERIODS: {
  id: TimePeriod;
  label: string;
}[] = [{
  id: 'Morning',
  label: 'Morning'
}, {
  id: 'Afternoon',
  label: 'Afternoon'
}, {
  id: 'Evening',
  label: 'Evening'
}, {
  id: 'Night',
  label: 'Night'
}];
function getTimePeriod(timeStr: string): TimePeriod {
  // Parse "10:30 AM", "2:05 PM", "8:45 PM" etc.
  const isPM = timeStr.toUpperCase().includes('PM');
  const parts = timeStr.replace(/\s*(AM|PM)/i, '').split(':');
  let hour = parseInt(parts[0]);
  if (isPM && hour !== 12) hour += 12;
  if (!isPM && hour === 12) hour = 0;
  if (hour < 12) return 'Morning';
  if (hour < 16) return 'Afternoon';
  if (hour < 20) return 'Evening';
  return 'Night';
}
export function SeatMap() {
  const [selectedPeriod, setSelectedPeriod] = useState<TimePeriod>('Morning');
  return <div className="bg-white rounded-xl soft-shadow p-6 border border-gray-100 h-full flex flex-col" data-id="element-129">
      <div className="flex justify-between items-center mb-6" data-id="element-130">
        <h2 className="text-lg font-semibold text-gray-900" data-id="element-131">
          Screen Occupancy
        </h2>
      </div>

      {/* Time Period Toggle */}
      <div className="flex gap-2 mb-6 overflow-x-auto hide-scrollbar pb-1" data-id="element-132">
        {TIME_PERIODS.map(period => <button key={period.id} onClick={() => setSelectedPeriod(period.id)} className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${selectedPeriod === period.id ? 'text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} style={selectedPeriod === period.id ? {
        backgroundColor: 'var(--accent-color)'
      } : {}} data-id="element-133">
            {period.label}
          </button>)}
      </div>

      {/* Screens List */}
      <div className="flex-1 flex flex-col gap-5 overflow-y-auto pr-2 hide-scrollbar" data-id="element-134">
        <AnimatePresence mode="wait" data-id="element-135">
          <motion.div key={selectedPeriod} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.2
        }} className="space-y-5" data-id="element-136">
            {SCREEN_OCCUPANCY.map(screen => {
            // Find shows in the selected time period
            const periodShows = screen.shows.filter(s => getTimePeriod(s.time) === selectedPeriod);
            if (periodShows.length === 0) return null;
            // Show the highest-occupancy show for this period
            const bestShow = periodShows.reduce((a, b) => a.occupancy > b.occupancy ? a : b);
            return <div key={screen.id} className="flex flex-col gap-2" data-id="element-137">
                  <div className="flex justify-between items-end" data-id="element-138">
                    <div data-id="element-139">
                      <span className="text-sm font-semibold text-gray-900 mr-2" data-id="element-140">
                        {screen.name}
                      </span>
                      <span className="text-xs text-gray-500" data-id="element-141">
                        {bestShow.film}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5" data-id="element-142">
                      <span className="text-[10px] text-gray-400" data-id="element-143">
                        {bestShow.time}
                      </span>
                      <span className="text-sm font-bold text-gray-900" data-id="element-144">
                        {bestShow.occupancy}%
                      </span>
                    </div>
                  </div>
                  <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden" data-id="element-145">
                    <motion.div initial={{
                  width: 0
                }} animate={{
                  width: `${bestShow.occupancy}%`
                }} transition={{
                  duration: 0.5,
                  ease: 'easeOut'
                }} className="h-full rounded-full" style={{
                  backgroundColor: bestShow.occupancy >= 90 ? 'var(--accent-color)' : bestShow.occupancy >= 70 ? '#10B981' : '#F59E0B'
                }} data-id="element-146" />
                  </div>
                  {/* Show additional shows in this period as small chips */}
                  {periodShows.length > 1 && <div className="flex flex-wrap gap-1.5 mt-0.5" data-id="element-147">
                      {periodShows.filter(s => s !== bestShow).map((show, idx) => <span key={idx} className="text-[10px] text-gray-500 bg-gray-50 px-2 py-0.5 rounded-full" data-id="element-148">
                            {show.time} · {show.film} · {show.occupancy}%
                          </span>)}
                    </div>}
                </div>;
          })}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>;
}